import { X, Building2, Check, CircleMinus } from '@/components/icons'
import { Button } from './ui/button'
import { MenuItem } from './ui/menu-item'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { ListSkeleton } from './ui/skeleton-presets'
import { useFbosByAirport } from '@/lib/hooks'
import type { FboRecord } from './FboSection'

interface FboPickerDrawerProps {
  isOpen: boolean
  onClose: () => void
  icao: string
  title?: string
  selectedFboId: string | null
  onSelect: (fboId: string | null) => void
}

// Bottom-sheet picker listing the FBOs on file for an airport.
export function FboPickerDrawer({
  isOpen,
  onClose,
  icao,
  title = 'Select FBO',
  selectedFboId,
  onSelect,
}: FboPickerDrawerProps) {
  const { data, isLoading } = useFbosByAirport(isOpen ? icao : undefined)
  const fbos = (data ?? []) as FboRecord[]

  const handleSelect = (fboId: string | null) => {
    onSelect(fboId)
    onClose()
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-h-[85dvh] flex flex-col bg-surface-L0 shadow-lg">
        <DrawerHeader className="flex flex-row items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {title} · {icao}
          </DrawerTitle>
          <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        <div
          className="max-h-[400px] overflow-y-auto overscroll-contain px-4 pb-[calc(1rem+var(--safe-bottom))]"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {isLoading ? (
            <ListSkeleton />
          ) : fbos.length === 0 ? (
            <p className="py-8 text-center text-sm text-content-primary-subtle">
              No FBOs on file for this airport.
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {fbos.map((fbo) => (
                <MenuItem
                  key={fbo.id}
                  size="large"
                  type="button"
                  onClick={() => handleSelect(fbo.id)}
                  leftIcon={<Building2 />}
                  secondaryText={
                    [fbo.vhfFrequency, fbo.phone].filter(Boolean).join(' · ') || undefined
                  }
                  rightIcon={
                    fbo.id === selectedFboId ? (
                      <Check className="text-content-brand-bold" />
                    ) : undefined
                  }
                >
                  {fbo.name}
                </MenuItem>
              ))}

              {selectedFboId && (
                <MenuItem
                  size="large"
                  type="button"
                  onClick={() => handleSelect(null)}
                  leftIcon={<CircleMinus className="text-content-primary-subtle" />}
                >
                  No FBO
                </MenuItem>
              )}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
